import React, { useState, useRef, useEffect } from "react";
import { Camera, CheckCircle, Circle, Upload, Signature, ArrowRight, Plus, MapPin, Calendar, Trash } from "@phosphor-icons/react";
import SignatureCanvas from "react-signature-canvas";
import { api } from "../../utils/api";
import { comprimirImagen } from "../../utils/imagenes";
import { Button } from "../shared/Button";
import { Input, Textarea } from "../shared/Input";
import { Modal } from "../shared/Modal";
import { Loading, EmptyState } from "../shared/EmptyState";

const TIPOS_REFORMA_LABEL = {
  bano: "🚿 Baño", cocina: "🍳 Cocina", suelo: "🪵 Suelo",
  pintura: "🎨 Pintura", integral: "🏠 Integral", otro: "📋 Otro",
};

export const VisitaTecnica = ({ adminToken }) => {
  const [vista, setVista] = useState("lista"); // "lista" | "form"
  const [visitas, setVisitas] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [visitaSeleccionada, setVisitaSeleccionada] = useState(null);

  useEffect(() => {
    if (vista === "lista") cargarVisitas();
  }, [vista]);

  const cargarVisitas = async () => {
    setCargandoLista(true);
    try {
      const data = await api.get("/visitas-tecnicas", adminToken);
      setVisitas([...data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setCargandoLista(false);
    }
  };

  const eliminarVisita = async (id) => {
    if (!window.confirm("¿Eliminar esta visita técnica?")) return;
    try {
      await api.delete(`/visitas-tecnicas/${id}`, adminToken);
      setVisitaSeleccionada(null);
      cargarVisitas();
    } catch (e) {
      alert("No se pudo eliminar la visita.");
    }
  };

  if (vista === "lista") {
    return (
      <div className="pb-24">
        <div className="container pt-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-h2 font-display">Visitas Técnicas</h1>
            <Button variant="accent" onClick={() => setVista("form")} icon={<Plus size={20} />}>Nueva</Button>
          </div>

          {cargandoLista ? (
            <Loading />
          ) : visitas.length === 0 ? (
            <EmptyState icon="📋" title="Sin visitas" description="Registrá tu primera visita técnica" action={<Button variant="accent" onClick={() => setVista("form")}>Nueva visita</Button>} />
          ) : (
            <div className="space-y-3">
              {visitas.map((v) => (
                <div key={v.id} className="card p-4 cursor-pointer card-interactive" onClick={() => setVisitaSeleccionada(v)}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold text-small">{v.cliente_nombre}</div>
                    <div className="text-tiny">{TIPOS_REFORMA_LABEL[v.tipo_reforma] || v.tipo_reforma}</div>
                  </div>
                  <div className="text-tiny text-secondary flex items-center gap-1"><MapPin size={12} /> {v.direccion}</div>
                  <div className="text-tiny text-secondary flex items-center gap-1 mt-1"><Calendar size={12} /> {v.fecha_visita}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <Modal isOpen={!!visitaSeleccionada} onClose={() => setVisitaSeleccionada(null)} title={visitaSeleccionada?.cliente_nombre || ""}>
          {visitaSeleccionada && (
            <div className="space-y-4">
              <div className="text-small">
                <div><strong>Teléfono:</strong> {visitaSeleccionada.cliente_telefono}</div>
                <div><strong>Dirección:</strong> {visitaSeleccionada.direccion}</div>
                <div><strong>Tipo:</strong> {TIPOS_REFORMA_LABEL[visitaSeleccionada.tipo_reforma] || visitaSeleccionada.tipo_reforma}</div>
                <div><strong>Fecha visita:</strong> {visitaSeleccionada.fecha_visita}</div>
                <div><strong>Técnico:</strong> {visitaSeleccionada.tecnico}</div>
              </div>

              {visitaSeleccionada.checklist && (
                <div>
                  <div className="text-label mb-2">CHECKLIST</div>
                  <div className="space-y-1">
                    {Object.entries(visitaSeleccionada.checklist).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-2 text-small">
                        {val ? <CheckCircle size={16} weight="fill" style={{ color: "var(--brand-accent)" }} /> : <Circle size={16} style={{ color: "var(--text-secondary)" }} />}
                        {key.replace(/_/g, " ")}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {visitaSeleccionada.observaciones && (
                <div>
                  <div className="text-label mb-1">OBSERVACIONES</div>
                  <div className="text-small">{visitaSeleccionada.observaciones}</div>
                </div>
              )}

              {visitaSeleccionada.fotos?.length > 0 && (
                <div>
                  <div className="text-label mb-2">FOTOS</div>
                  <div className="grid grid-cols-2 gap-2">
                    {visitaSeleccionada.fotos.map((url, i) => (
                      <img key={i} src={url} alt={`Foto ${i + 1}`} style={{ width: "100%", borderRadius: 8 }} />
                    ))}
                  </div>
                </div>
              )}

              {visitaSeleccionada.firma_cliente && (
                <div>
                  <div className="text-label mb-1">FIRMA</div>
                  <img src={visitaSeleccionada.firma_cliente} alt="Firma" style={{ maxWidth: 300, border: "1px solid var(--border-subtle)", borderRadius: 8 }} />
                </div>
              )}

              <Button variant="ghost" onClick={() => eliminarVisita(visitaSeleccionada.id)} icon={<Trash size={16} />}>
                Eliminar visita
              </Button>
            </div>
          )}
        </Modal>
      </div>
    );
  }

  return <FormularioVisita adminToken={adminToken} onTerminado={() => setVista("lista")} onCancelar={() => setVista("lista")} />;
};

const FormularioVisita = ({ adminToken, onTerminado, onCancelar }) => {
  const [paso, setPaso] = useState(1);
  const [visita, setVisita] = useState({
    cliente_nombre: "",
    cliente_telefono: "",
    direccion: "",
    tipo_reforma: "bano",
    fecha_visita: new Date().toISOString().split('T')[0],
    observaciones: "",
    checklist: {
      acceso_facil: false,
      medidas_tomadas: false,
      servicios_activos: false,
      mobiliario_retirar: false,
      permisos_necesarios: false,
    },
    fotos: [],
    firma_cliente: "",
    tecnico: "Andy Gonzaga"
  });

  const sigPad = useRef(null);
  const [modalFirma, setModalFirma] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const tiposReforma = [
    { value: "bano", label: "🚿 Baño" },
    { value: "cocina", label: "🍳 Cocina" },
    { value: "suelo", label: "🪵 Suelo" },
    { value: "pintura", label: "🎨 Pintura" },
    { value: "integral", label: "🏠 Integral" },
    { value: "otro", label: "📋 Otro" }
  ];

  const checklistItems = [
    { key: "acceso_facil", label: "Acceso fácil al inmueble", icon: "🚪" },
    { key: "medidas_tomadas", label: "Medidas tomadas correctamente", icon: "📏" },
    { key: "servicios_activos", label: "Servicios activos (luz, agua)", icon: "⚡" },
    { key: "mobiliario_retirar", label: "Mobiliario a retirar identificado", icon: "📦" },
    { key: "permisos_necesarios", label: "Permisos necesarios confirmados", icon: "📄" }
  ];

  const handleFotoChange = async (e) => {
    const files = Array.from(e.target.files);
    // Antes usaba URL.createObjectURL(file) — una referencia temporal del
    // navegador que se guardaba tal cual en la base. Se veía bien en el
    // momento, pero esa url deja de existir al recargar la página o abrir
    // la visita en otro momento/dispositivo: las fotos "desaparecían".
    // Ahora se comprimen a base64 real, igual que el resto de la app.
    for (const file of files) {
      try {
        const comprimida = await comprimirImagen(file);
        setVisita(prev => ({ ...prev, fotos: [...prev.fotos, { id: Date.now() + Math.random(), url: comprimida }] }));
      } catch (err) {
        console.error("Error comprimiendo foto:", err);
      }
    }
  };

  const eliminarFoto = (id) => {
    setVisita(prev => ({
      ...prev,
      fotos: prev.fotos.filter(f => f.id !== id)
    }));
  };

  const toggleCheck = (key) => {
    setVisita(prev => ({
      ...prev,
      checklist: {
        ...prev.checklist,
        [key]: !prev.checklist[key]
      }
    }));
  };

  const guardarFirma = () => {
    if (sigPad.current) {
      const firmaBase64 = sigPad.current.toDataURL();
      setVisita(prev => ({ ...prev, firma_cliente: firmaBase64 }));
      setModalFirma(false);
    }
  };

  const limpiarFirma = () => {
    if (sigPad.current) {
      sigPad.current.clear();
    }
  };

  const guardarVisita = async () => {
    setGuardando(true);
    try {
      // Subir fotos primero (simulado - en producción usar S3/Cloudinary)
      const fotosUrls = visita.fotos.map(f => f.url);
      
      const visitaData = {
        ...visita,
        fotos: fotosUrls,
        id: `VT-${Date.now()}`,
        created_at: new Date().toISOString()
      };

      await api.post("/visitas-tecnicas", visitaData, adminToken);

      alert("✅ Visita técnica guardada correctamente");
      onTerminado();
    } catch (e) {
      alert("Error al guardar visita: " + e.message);
    } finally {
      setGuardando(false);
    }
  };

  const puedeAvanzar = () => {
    switch(paso) {
      case 1:
        return visita.cliente_nombre && visita.cliente_telefono && visita.direccion;
      case 2:
        return true; // Checklist es opcional
      case 3:
        return true; // Fotos opcionales
      case 4:
        return visita.firma_cliente;
      default:
        return false;
    }
  };

  return (
    <div className="pb-24">
      <div className="container pt-4">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-h2 font-display mb-2">Nueva Visita Técnica</h1>
            <div className="text-small text-secondary">Paso {paso} de 4</div>
          </div>
          <button className="text-tiny" style={{ textDecoration: "underline", color: "var(--text-secondary)" }} onClick={onCancelar}>
            Cancelar
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-6 flex gap-2">
          {[1,2,3,4].map(num => (
            <div 
              key={num} 
              className="h-2 flex-1 rounded-full" 
              style={{ 
                background: paso >= num ? "var(--brand-accent)" : "var(--border-subtle)" 
              }}
            />
          ))}
        </div>

        {/* Paso 1: Datos cliente */}
        {paso === 1 && (
          <div className="space-y-4">
            <div className="card p-4">
              <h3 className="text-h3 font-display mb-4">📋 Datos del Cliente</h3>
              <Input
                label="Nombre del cliente"
                placeholder="Nombre completo"
                value={visita.cliente_nombre}
                onChange={(e) => setVisita(prev => ({ ...prev, cliente_nombre: e.target.value }))}
              />
              <Input
                label="Teléfono"
                type="tel"
                placeholder="6XX XXX XXX"
                value={visita.cliente_telefono}
                onChange={(e) => setVisita(prev => ({ ...prev, cliente_telefono: e.target.value }))}
              />
              <Input
                label="Dirección de la obra"
                placeholder="Calle, número, piso, población"
                value={visita.direccion}
                onChange={(e) => setVisita(prev => ({ ...prev, direccion: e.target.value }))}
              />
              <div className="mb-3">
                <label className="text-label mb-2 block">Tipo de reforma</label>
                <select
                  value={visita.tipo_reforma}
                  onChange={(e) => setVisita(prev => ({ ...prev, tipo_reforma: e.target.value }))}
                  className="w-full p-3 rounded-lg border border-subtle bg-surface-secondary"
                >
                  {tiposReforma.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
              </div>
              <Input
                label="Fecha de visita"
                type="date"
                value={visita.fecha_visita}
                onChange={(e) => setVisita(prev => ({ ...prev, fecha_visita: e.target.value }))}
              />
            </div>
          </div>
        )}

        {/* Paso 2: Checklist */}
        {paso === 2 && (
          <div className="space-y-4">
            <div className="card p-4">
              <h3 className="text-h3 font-display mb-4">✅ Checklist de Inspección</h3>
              <div className="space-y-3">
                {checklistItems.map(item => (
                  <div
                    key={item.key}
                    onClick={() => toggleCheck(item.key)}
                    className="card p-4 cursor-pointer card-interactive flex items-center gap-3"
                    style={{ background: "var(--surface-secondary)" }}
                  >
                    {visita.checklist[item.key] ? (
                      <CheckCircle size={24} weight="fill" style={{ color: "var(--brand-accent)" }} />
                    ) : (
                      <Circle size={24} style={{ color: "var(--text-secondary)" }} />
                    )}
                    <div>
                      <div className="text-small font-bold">{item.icon} {item.label}</div>
                    </div>
                  </div>
                ))}
              </div>
              <Textarea
                label="Observaciones adicionales"
                placeholder="Anotaciones importantes sobre la visita..."
                value={visita.observaciones}
                onChange={(e) => setVisita(prev => ({ ...prev, observaciones: e.target.value }))}
                rows={4}
              />
            </div>
          </div>
        )}

        {/* Paso 3: Fotos */}
        {paso === 3 && (
          <div className="space-y-4">
            <div className="card p-4">
              <h3 className="text-h3 font-display mb-4">📸 Fotos de la Obra</h3>
              
              <label className="card p-6 text-center cursor-pointer card-interactive block mb-4">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFotoChange}
                  className="hidden"
                />
                <Camera size={48} style={{ margin: "0 auto 8px", color: "var(--brand-accent)" }} />
                <div className="text-small font-bold">Añadir fotos</div>
                <div className="text-tiny text-secondary">Click para seleccionar</div>
              </label>

              {visita.fotos.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {visita.fotos.map(foto => (
                    <div key={foto.id} className="relative">
                      <img src={foto.url} alt="Foto obra" className="w-full h-32 object-cover rounded-lg" />
                      <button
                        onClick={() => eliminarFoto(foto.id)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Paso 4: Firma */}
        {paso === 4 && (
          <div className="space-y-4">
            <div className="card p-4">
              <h3 className="text-h3 font-display mb-4">✍️ Firma del Cliente</h3>
              
              {visita.firma_cliente ? (
                <div className="text-center">
                  <img src={visita.firma_cliente} alt="Firma" className="border border-subtle rounded-lg mx-auto mb-4" style={{ maxWidth: "300px" }} />
                  <Button variant="outline" onClick={() => setModalFirma(true)}>
                    Cambiar firma
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-small text-secondary mb-4">El cliente debe firmar para confirmar la visita</div>
                  <Button variant="accent" onClick={() => setModalFirma(true)} icon={<Signature size={20} />}>
                    Abrir panel de firma
                  </Button>
                </div>
              )}

              <div className="card p-3 mt-4" style={{ background: "var(--surface-secondary)" }}>
                <div className="text-tiny text-secondary">
                  📌 La firma confirma que el cliente ha recibido la visita técnica y acepta las condiciones de la obra
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botones navegación */}
        <div className="flex gap-3 mt-6">
          {paso > 1 && (
            <Button variant="outline" onClick={() => setPaso(paso - 1)}>
              Atrás
            </Button>
          )}
          {paso < 4 ? (
            <Button 
              variant="primary" 
              className="flex-1" 
              onClick={() => setPaso(paso + 1)}
              disabled={!puedeAvanzar()}
              icon={<ArrowRight size={20} />}
            >
              Continuar
            </Button>
          ) : (
            <Button 
              variant="accent" 
              className="flex-1" 
              onClick={guardarVisita}
              disabled={!puedeAvanzar() || guardando}
            >
              {guardando ? "Guardando..." : "Guardar Visita"}
            </Button>
          )}
        </div>
      </div>

      {/* Modal Firma */}
      <Modal isOpen={modalFirma} onClose={() => setModalFirma(false)} title="Firma del Cliente">
        <div>
          <div className="border border-subtle rounded-lg mb-3" style={{ background: "white" }}>
            <SignatureCanvas
              ref={sigPad}
              canvasProps={{
                width: 400,
                height: 200,
                className: "signature-canvas"
              }}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={limpiarFirma}>
              Limpiar
            </Button>
            <Button variant="accent" className="flex-1" onClick={guardarFirma}>
              Guardar Firma
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
